import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  FormButton,
  FormControl,
  openDialog,
} from 'platform/components';
import {HStack, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {isNil, isNotNil, last} from 'ramda';

import {
  Payment,
  PaymentAllowedAction,
  usePostCheckoutPaymentFullCorrectionCancelMutation,
  usePostCheckoutPaymentFullCorrectionDiscardMutation,
  usePostCheckoutPaymentPartialCorrectionCancelMutation,
  usePostCheckoutPaymentPartialCorrectionDiscardMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {ContextTarget, RestrictionActionContext} from '../constants/issueRestrictionAlerts';
import {useInvalidateServiceOrderJobs} from '../hooks/useInvalidateServiceOrderJobs';
import {CheckoutPaymentFormType} from '../types/CheckoutPaymentFormType';
import {PayDialog} from './PayDialog';

interface CheckoutPaymentActionsProps extends RequiredTestIdProps {
  allowedActions: PaymentAllowedAction[];
  checkoutId: string;
  hasIssueRestrictions?: boolean;
  payment: Payment;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  onAddAnotherDepositClick?: () => Promise<unknown>;
  onRemoveDepositClick?: () => Promise<unknown>;
  isIssueLoading?: boolean;
  isLazySaving?: boolean;
  isSubmitButtonDisabled?: boolean;
  control?: FormControl<CheckoutPaymentFormType>;
  contextTarget?: ContextTarget;
  contextId?: string;
  actionContext?: RestrictionActionContext;
  templateDropdown?: ReactNode;
  serviceOrderId?: string;
}

export function CheckoutPaymentActions(props: CheckoutPaymentActionsProps) {
  const invalidateServiceOrderJobs = useInvalidateServiceOrderJobs(props.serviceOrderId);

  const [postFullCorrectionCancel, {isLoading: isFullCorrectionCancelLoading}] =
    usePostCheckoutPaymentFullCorrectionCancelMutation();
  const [postPartialCorrectionCancel, {isLoading: isPartialCorrectionCancelLoading}] =
    usePostCheckoutPaymentPartialCorrectionCancelMutation();
  const [postFullCorrectionDiscard, {isLoading: isFullCorrectionDiscardLoading}] =
    usePostCheckoutPaymentFullCorrectionDiscardMutation();
  const [postPartialCorrectionDiscard, {isLoading: isPartialCorrectionDiscardLoading}] =
    usePostCheckoutPaymentPartialCorrectionDiscardMutation();

  const [isAddLoading, startAddLoading, stopAddLoading] = useBoolean();
  const [isRemoveLoading, startRemoveLoading, stopRemoveLoading] = useBoolean();

  const control = props.control;

  const canPay =
    props.allowedActions.includes('PAY') && props.payment.method !== 'CASH_ON_DELIVERY';
  const canRemoveDeposit =
    isNotNil(props.onRemoveDepositClick) && props.allowedActions.includes('REMOVE_PAYMENT');
  const canIssueInvoice =
    props.allowedActions.includes('ISSUE_INVOICE') ||
    props.allowedActions.includes('ISSUE_FULL_CORRECTION') ||
    props.allowedActions.includes('ISSUE_PARTIAL_CORRECTION');
  const canSelectTemplate = props.allowedActions.includes('SELECT_TEMPLATE');
  const canCancelDocument =
    props.allowedActions.includes('CANCEL_FULL_CORRECTION') ||
    props.allowedActions.includes('CANCEL_PARTIAL_CORRECTION');
  const canDiscardCorrection =
    props.allowedActions.includes('DISCARD_FULL_CORRECTION') ||
    props.allowedActions.includes('DISCARD_PARTIAL_CORRECTION');
  const isCancelDocumentLoading = isFullCorrectionCancelLoading || isPartialCorrectionCancelLoading;
  const isDiscardLoading = isFullCorrectionDiscardLoading || isPartialCorrectionDiscardLoading;

  const correctionArg = {
    checkoutId: props.checkoutId,
    paymentId: props.payment.checkoutPaymentId,
  };

  const handleCancelDocument = () => {
    match(props.allowedActions)
      .when(
        (actions) => actions.includes('CANCEL_FULL_CORRECTION'),
        () => postFullCorrectionCancel(correctionArg).unwrap().catch(handleApiError)
      )
      .when(
        (actions) => actions.includes('CANCEL_PARTIAL_CORRECTION'),
        () =>
          postPartialCorrectionCancel(correctionArg)
            .unwrap()
            .then(invalidateServiceOrderJobs)
            .catch(handleApiError)
      )
      .otherwise(() => undefined);
  };

  const handleDiscardCorrection = () => {
    match(props.allowedActions)
      .when(
        (actions) => actions.includes('DISCARD_FULL_CORRECTION'),
        () => postFullCorrectionDiscard(correctionArg).unwrap().catch(handleApiError)
      )
      .when(
        (actions) => actions.includes('DISCARD_PARTIAL_CORRECTION'),
        () =>
          postPartialCorrectionDiscard(correctionArg)
            .unwrap()
            .then(invalidateServiceOrderJobs)
            .catch(handleApiError)
      )
      .otherwise(() => undefined);
  };

  const handlePay = () => {
    openDialog(
      <PayDialog
        checkoutId={props.checkoutId}
        payment={props.payment}
        onClose={closeCurrentDialog}
        data-testid={suffixTestId('payDialog', props)}
      />,
      {
        title: i18n.t('entity.invoice.labels.paymentOfInvoice', {
          number: last(props.payment.files ?? [])?.name,
        }),
      }
    );
  };

  const handleAddAnotherDeposit = () => {
    if (isNil(props.onAddAnotherDepositClick)) {
      return;
    }

    startAddLoading();
    props.onAddAnotherDepositClick().finally(stopAddLoading);
  };

  const handleRemoveDeposit = () => {
    if (isNil(props.onRemoveDepositClick)) {
      return;
    }

    startRemoveLoading();
    props.onRemoveDepositClick().finally(stopRemoveLoading);
  };

  return (
    <VStack spacing={4} width="100%">
      <HStack width="100%">
        <Show when={props.onAddAnotherDepositClick}>
          <Button
            variant="link"
            title={i18n.t('entity.checkout.addAnotherPayment')}
            leftIcon="content/add_circle"
            onClick={handleAddAnotherDeposit}
            isDisabled={isAddLoading || isRemoveLoading || props.isEditingDisabled}
            isLoading={isAddLoading}
            data-testid={suffixTestId('addAnotherDeposit', props)}
          />
        </Show>
        <Space fillAvailable />
        <HStack spacing={3} align="center">
          <Show when={canRemoveDeposit}>
            <Button
              variant="dangerLink"
              title={i18n.t('entity.checkout.removePayment')}
              onClick={handleRemoveDeposit}
              isDisabled={isRemoveLoading || props.isEditingDisabled || props.isLazySaving}
              isLoading={isRemoveLoading}
              data-testid={suffixTestId('removeDeposit', props)}
            />
          </Show>
          <Show when={canSelectTemplate}>{props.templateDropdown}</Show>
          <ButtonGroup>
            <Show when={canCancelDocument}>
              <Button
                variant="dangerLink"
                size="small"
                title={i18n.t('entity.invoice.actions.cancelDocument')}
                onClick={handleCancelDocument}
                isDisabled={props.isEditingDisabled || props.isCompletingDisabled}
                isLoading={isCancelDocumentLoading}
                data-testid={suffixTestId('cancelDocument', props)}
              />
            </Show>
            <Show when={canDiscardCorrection}>
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={handleDiscardCorrection}
                isDisabled={props.isEditingDisabled || props.isCompletingDisabled}
                isLoading={isDiscardLoading}
                data-testid={suffixTestId('discardCorrection', props)}
              />
            </Show>
            <Show when={canPay}>
              <Button
                title={i18n.t('entity.order.actions.pay')}
                onClick={handlePay}
                isDisabled={
                  isRemoveLoading || props.isEditingDisabled || props.isCompletingDisabled
                }
                data-testid={suffixTestId('pay', props)}
              />
            </Show>
            <Show when={canIssueInvoice && isNotNil(control) ? control : null}>
              {(control) => (
                <FormButton
                  control={control}
                  type="submit"
                  isLoading={props.isIssueLoading}
                  isDisabled={
                    props.isSubmitButtonDisabled ||
                    isRemoveLoading ||
                    props.hasIssueRestrictions ||
                    props.isCompletingDisabled ||
                    props.isIssueLoading
                  }
                  title={i18n.t('entity.checkout.actions.issuePayment')}
                  data-testid={suffixTestId('issuePayment', props)}
                />
              )}
            </Show>
          </ButtonGroup>
        </HStack>
      </HStack>
    </VStack>
  );
}
